import { defineStore } from 'pinia'

import UserResource from '@/api/resources/user'

import { useLoadingStore } from '@/store/loading'
import { useSessionStore } from './session'

import { IAccountActivationParams, IChangeDefaultCompany, ILoginParams, IRequestActivationTokenParams, IUpdateProfileParams } from '@/interfaces/api/user'
import { ICreateUser, IUser, IUserLight } from '@/interfaces/entities/user'
import { patchUserLightFromPayload } from '@/utils/user/company-user-update'
import { UserRoles } from '@/interfaces/enums'
import { IForgotPassword, IUpdatePassword } from '@/interfaces/stores/user'

import { IApiCallbacks, ILoginCallbacks } from '@/interfaces/api/api.interface'
import { AnalyticsEvents, logFirebaseAnalytics } from '@/utils/firebase'
import { useCompanyStore } from './company'

export const useUserStore = defineStore({
  id: 'users',
  // `temporaryPassword` deliberadamente fora do persist — eh um segredo de
  // curta duracao usado apenas entre a tela de login (quando o backend
  // sinaliza requires_password_change) e a tela de redefinicao. Se o
  // usuario fechar o navegador no meio, faz login de novo. Sem F5 tudo bem.
  persist: { paths: ['currentUser', 'currentRole'] },

  state: () => ({
    currentUser: {} as IUser,
    currentRole: UserRoles.USER as UserRoles,
    temporaryPassword: '' as string,
    users: [] as IUserLight[],
  }),

  getters: {
    getCurrentUser: (state) => state.currentUser,
    getCurrentRole: (state) => state.currentRole,
    getHasNewDatepicker: (state) => state.currentUser.has_new_datepicker,
    getUsers: (state) => state.users,
  },

  actions: {
    login(payload: ILoginParams, { onSuccess, onFailure, onCompletion, onRequiresPasswordChange }: ILoginCallbacks) {
      UserResource.login(payload)
        .then((response) => {
          if (response.active_account == false) return onFailure!()

          // Login feito com senha temporaria — backend sinaliza para a UI
          // forcar a redefinicao antes de criar sessao. Espelha o fluxo
          // do bem_app (vide api/app/v1/sessions_controller.rb).
          if (response.requires_password_change) {
            this.temporaryPassword = response.temporary_password ?? ''
            // NAO criar sessao nem armazenar usuario — apenas levar para a
            // tela de redefinicao. O JWT volta a ser obtido apos o re-login.
            return onRequiresPasswordChange ? onRequiresPasswordChange() : onSuccess!()
          }

          useSessionStore().createSession(response.token, response.email, response.has_artificial_intelligence, response.phone_number)
          useCompanyStore().setCompanies(response.companies)
          response.companies.length ? useCompanyStore().setCurrentCompany(response.default_company_id ?? response.companies[0].id) : null
          this.currentUser = response
          logFirebaseAnalytics(AnalyticsEvents.AUTH.LOGIN)
          onSuccess!()
        })
        .catch((error) => {
          // onFailure!(error.data.errors)
        })
        .finally(() => {
          onCompletion!()
        })
    },

    setTemporaryPassword(value: string) {
      this.temporaryPassword = value
    },

    clearTemporaryPassword() {
      this.temporaryPassword = ''
    },

    createUser(payload: ICreateUser, { onSuccess, onFailure, onCompletion }: IApiCallbacks) {
      useLoadingStore().initLoading()
      UserResource.create(payload)
        .then((response) => {
          useSessionStore().createSession(response.token, response.email, response.has_artificial_intelligence)
          this.currentUser = response
          logFirebaseAnalytics(AnalyticsEvents.AUTH.SIGNUP)
          onSuccess!()
        })
        .catch((error) => {
          onFailure!(error.data.errors)
        })
        .finally(() => {
          onCompletion!()
          useLoadingStore().removeLoading()
        })
    },

    forgotPassword(payload: IForgotPassword, { onSuccess, onFailure, onCompletion }: IApiCallbacks) {
      logFirebaseAnalytics(AnalyticsEvents.AUTH.FORGOT_PASSWORD)

      UserResource.forgotPassword(payload)
        .then(() => {
          onSuccess!()
        })
        .catch((error) => {
          onFailure!(error.data.errors)
        })
        .finally(() => {
          onCompletion!()
        })
    },

    updatePassword(payload: IUpdatePassword, { onSuccess, onFailure, onCompletion }: IApiCallbacks) {
      useLoadingStore().initLoading()

      logFirebaseAnalytics(AnalyticsEvents.AUTH.UPDATE_PASSWORD)

      UserResource.updatePassword(payload)
        .then(() => {
          onSuccess!()
        })
        .catch((error) => {
          onFailure!(error.data.errors)
        })
        .finally(() => {
          onCompletion!()
        })
    },

    activateAccount(payload: IAccountActivationParams, { onSuccess, onFailure, onCompletion }: IApiCallbacks) {
      useLoadingStore().initLoading()

      logFirebaseAnalytics(AnalyticsEvents.AUTH.ACTIVATE_ACCOUNT)

      UserResource.activateAccount(payload)
        .then((response) => {
          onSuccess!(response)
        })
        .catch(() => {})
        .finally(() => {
          onCompletion!()
        })
    },

    requestActivationToken(payload: IRequestActivationTokenParams, { onSuccess, onFailure, onCompletion }: IApiCallbacks) {
      logFirebaseAnalytics(AnalyticsEvents.AUTH.REQUEST_ACTIVATION_TOKEN)

      UserResource.requestActivationToken(payload)
        .then((response) => {
          onSuccess!(response)
        })
        .catch((error) => {
          onFailure!(error.data.errors)
        })
        .finally(() => {
          onCompletion!()
        })
    },

    changeDefaultCompany(payload: IChangeDefaultCompany) {
      UserResource.changeDefaultCompany(payload)
    },

    async fetchCurrentUser() {
      const response = await UserResource.getProfileData()
      this.currentUser = response
    },

    async fetchUsers() {
      try {
        const response = await UserResource.getUsers()
        this.users = response || []
      } catch {
        this.users = []
      }
    },

    async updateProfile(payload: IUpdateProfileParams) {
      const response = await UserResource.update(payload)
      this.currentUser = response
      return response
    },

    /** Atualiza usuário da empresa (lista + sessão se for o usuário logado). Usado na lista e no modal. */
    async updateCompanyUser(payload: IUpdateProfileParams) {
      const response = (await UserResource.update(payload)) as IUser
      const index = this.users.findIndex((u) => u.id === payload.id)
      if (index !== -1) {
        this.users[index] = patchUserLightFromPayload(this.users[index], payload)
      }
      if (this.currentUser?.id === payload.id) {
        this.currentUser = { ...this.currentUser, ...response }
      }
      return response
    },

    async uploadAvatar(file: File) {
      const response = await UserResource.uploadAvatar(this.currentUser.id, file)
      this.currentUser = response
      return response
    },

    async removeAvatar() {
      const response = await UserResource.removeAvatar(this.currentUser.id)
      this.currentUser = response
      return response
    },

    setCurrentRole(role: UserRoles) {
      this.currentRole = role
    },
  },
})
